import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAssetById } from '@/hooks/useAssetById';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Lock, Unlock, GripVertical, Edit3 } from 'lucide-react';
import { CloudinaryEditorModal } from './CloudinaryEditorModal';
import { updateAsset } from '@/utils/graphqlClient';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { stripCloudinaryTransformations } from '@/utils/cloudinaryUpload';

interface EmbeddedAssetViewerProps {
  assetId: string;
  onEditorModalStateChange?: (isOpen: boolean) => void;
  imageType?: 'markup' | 'cropped' | 'original';
  onImageTypeChange?: (imageType: 'markup' | 'cropped' | 'original') => void;
  hideTypeToggle?: boolean;
  hideEditButtons?: boolean;
  hideHeader?: boolean;
  onImageClick?: (image: 'before' | 'after') => void;
}

export const EmbeddedAssetViewer: React.FC<EmbeddedAssetViewerProps> = ({
  assetId,
  onEditorModalStateChange,
  imageType: controlledImageType,
  onImageTypeChange,
  hideTypeToggle = false,
  hideEditButtons = false,
  hideHeader = false,
  onImageClick
}) => {
  const { asset, loading, error } = useAssetById(assetId);
  const [internalImageType, setInternalImageType] = useState<'markup' | 'cropped' | 'original'>('original');
  const [hasInitialized, setHasInitialized] = useState(false);

  // Use controlled imageType if provided, otherwise use internal state
  const imageType = controlledImageType ?? internalImageType;
  const setImageType = (newType: 'markup' | 'cropped' | 'original') => {
    if (onImageTypeChange) {
      onImageTypeChange(newType);
    } else {
      setInternalImageType(newType);
    }
  };
  const [dragState, setDragState] = useState({ isDragging: false, startX: 0, startY: 0, scrollLeft: 0, scrollTop: 0 });
  const [isLocked, setIsLocked] = useState(true); // Lock is default mode
  const [activePanel, setActivePanel] = useState<'left' | 'right' | null>(null);

  // Resizer state
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // Percentage
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(50);

  // Refs for the container elements
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Editor modal state
  const [editorModal, setEditorModal] = useState<{
    isOpen: boolean;
    imageUrl: string;
    imageType: 'primary' | 'secondary';
  }>({
    isOpen: false,
    imageUrl: '',
    imageType: 'primary'
  });

  // Helper function to format version date (yyyymmdd -> readable date)
  const formatVersionDate = (versionStr: string | null | undefined): string => {
    if (!versionStr) return '';
    try {
      // Extract date part from version string (assuming format like "20241201" or "20241201_v1")
      const dateMatch = versionStr.match(/(\d{8})/);
      if (!dateMatch) return versionStr;

      const dateStr = dateMatch[1];
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);

      const date = new Date(`${year}-${month}-${day}`);
      return format(date, 'MMM d, yyyy');
    } catch {
      return versionStr;
    }
  };

  // Chronological ordering logic - same as other components
  const getChronologicalUrls = () => {
    if (!asset) return { beforeImageUrl: null, afterImageUrl: null, beforeVersion: null, afterVersion: null };

    // Helper functions to get URLs based on imageType
    const getPrimaryUrl = () => {
      switch (imageType) {
        case 'markup':
          return asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;
        case 'cropped':
          return asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;
        case 'original':
          // Try original URL first, then strip transformations from cropped URL, finally fallback to primary_image
          return asset.primary_original_url ||
                 stripCloudinaryTransformations(asset.primary_cropped_url) ||
                 stripCloudinaryTransformations(asset.primary_markup_url) ||
                 asset.primary_image;
        default:
          return asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;
      }
    };

    const getSecondaryUrl = () => {
      switch (imageType) {
        case 'markup':
          return asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image;
        case 'cropped':
          return asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image;
        case 'original':
          // Try original URL first, then strip transformations from cropped URL, finally fallback to secondary_image
          return asset.secondary_original_url ||
                 stripCloudinaryTransformations(asset.secondary_cropped_url) ||
                 stripCloudinaryTransformations(asset.secondary_markup_url) ||
                 asset.secondary_image;
        default:
          return asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image;
      }
    };

    const primaryDisplayUrl = getPrimaryUrl();
    const secondaryDisplayUrl = getSecondaryUrl();

    // Determine chronological order for before/after display
    const primaryDate = asset.version ? parseInt(asset.version) : 0;
    const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;

    // If primary is newer than secondary, we need to swap for chronological display
    const shouldSwapForChronology = primaryDate > secondaryDate && secondaryDisplayUrl;

    const beforeImageUrl = shouldSwapForChronology ? secondaryDisplayUrl : primaryDisplayUrl;
    const afterImageUrl = shouldSwapForChronology ? primaryDisplayUrl : secondaryDisplayUrl;
    const beforeVersion = shouldSwapForChronology ? asset.secondary_version : asset.version;
    const afterVersion = shouldSwapForChronology ? asset.version : asset.secondary_version;

    return { beforeImageUrl, afterImageUrl, beforeVersion, afterVersion };
  };

  // Get image URLs based on selected type with fallbacks (only if asset exists)
  const getPrimaryImageUrl = () => {
    if (!asset) return null;

    // In single mode, show whichever image exists (prefer secondary, then primary)
    if (asset.comparison_mode === 'single') {
      let secondaryUrl = null;
      let primaryUrl = null;

      switch (imageType) {
        case 'markup':
          secondaryUrl = asset.secondary_markup_url || asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image;
          primaryUrl = asset.primary_markup_url || asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;
          break;
        case 'cropped':
          secondaryUrl = asset.secondary_cropped_url || asset.secondary_original_url || asset.secondary_image;
          primaryUrl = asset.primary_cropped_url || asset.primary_original_url || asset.primary_image;
          break;
        case 'original':
          secondaryUrl = asset.secondary_original_url ||
                         stripCloudinaryTransformations(asset.secondary_cropped_url) ||
                         stripCloudinaryTransformations(asset.secondary_markup_url) ||
                         asset.secondary_image;
          primaryUrl = asset.primary_original_url ||
                       stripCloudinaryTransformations(asset.primary_cropped_url) ||
                       stripCloudinaryTransformations(asset.primary_markup_url) ||
                       asset.primary_image;
          break;
      }

      // Return whichever exists (prefer secondary, fallback to primary)
      return secondaryUrl || primaryUrl;
    }

    // For side-by-side mode, use chronological ordering
    const { afterImageUrl } = getChronologicalUrls();
    return afterImageUrl;
  };

  const getSecondaryImageUrl = () => {
    if (!asset) return null;
    const { beforeImageUrl } = getChronologicalUrls();
    return beforeImageUrl;
  };

  // Determine which actual image type (primary/secondary) is shown on each side after chronological swapping
  const getActualImageTypes = () => {
    if (!asset) return { beforeImageType: 'primary' as const, afterImageType: 'secondary' as const };

    const primaryDate = asset.version ? parseInt(asset.version) : 0;
    const secondaryDate = asset.secondary_version ? parseInt(asset.secondary_version) : 0;
    const secondaryDisplayUrl = asset.secondary_original_url ||
                                stripCloudinaryTransformations(asset.secondary_cropped_url) ||
                                stripCloudinaryTransformations(asset.secondary_markup_url) ||
                                asset.secondary_image;

    const shouldSwapForChronology = primaryDate > secondaryDate && secondaryDisplayUrl;

    // If swapped: before=secondary, after=primary
    // If not swapped: before=primary, after=secondary
    return {
      beforeImageType: shouldSwapForChronology ? ('secondary' as const) : ('primary' as const),
      afterImageType: shouldSwapForChronology ? ('primary' as const) : ('secondary' as const)
    };
  };

  // Check which image types are available (memoized to prevent re-renders)
  const availableTypes = useMemo(() => ({
    markup: !!(asset?.primary_markup_url || asset?.secondary_markup_url),
    cropped: !!(asset?.primary_cropped_url || asset?.secondary_cropped_url),
    original: !!(
      asset?.primary_original_url ||
      asset?.secondary_original_url ||
      stripCloudinaryTransformations(asset?.primary_cropped_url) ||
      stripCloudinaryTransformations(asset?.secondary_cropped_url) ||
      stripCloudinaryTransformations(asset?.primary_markup_url) ||
      stripCloudinaryTransformations(asset?.secondary_markup_url) ||
      asset?.primary_image ||
      asset?.secondary_image
    )
  }), [asset?.primary_markup_url, asset?.secondary_markup_url, asset?.primary_cropped_url, asset?.secondary_cropped_url, asset?.primary_original_url, asset?.secondary_original_url, asset?.primary_image, asset?.secondary_image]);

  // Auto-select the best available type on initial load only
  useEffect(() => {
    if (!asset || hasInitialized) return;

    // On initial load, prioritize cropped if available
    if (availableTypes.cropped) {
      setImageType('cropped');
    } else if (availableTypes.markup) {
      setImageType('markup');
    } else if (availableTypes.original) {
      setImageType('original');
    }

    setHasInitialized(true);
  }, [asset, availableTypes, hasInitialized]);

  // Validate that the current imageType is available, fallback if not
  useEffect(() => {
    if (!asset || !hasInitialized) return;

    // Check if current type is available, if not, fallback to available option
    if (imageType === 'markup' && !availableTypes.markup) {
      if (availableTypes.cropped) setImageType('cropped');
      else if (availableTypes.original) setImageType('original');
    } else if (imageType === 'cropped' && !availableTypes.cropped) {
      if (availableTypes.markup) setImageType('markup');
      else if (availableTypes.original) setImageType('original');
    } else if (imageType === 'original' && !availableTypes.original) {
      if (availableTypes.cropped) setImageType('cropped');
      else if (availableTypes.markup) setImageType('markup');
    }
  }, [asset, availableTypes, imageType, hasInitialized]);

  // Synchronize scroll positions when locked
  const syncScrollPosition = (sourcePanel: 'left' | 'right', scrollLeft: number, scrollTop: number) => {
    if (!isLocked) return;

    const targetRef = sourcePanel === 'left' ? rightPanelRef : leftPanelRef;
    if (targetRef.current) {
      targetRef.current.scrollLeft = scrollLeft;
      targetRef.current.scrollTop = scrollTop;
    }
  };

  // When lock is toggled on, sync left panel to right panel position
  const handleLockToggle = () => {
    if (!isLocked && leftPanelRef.current && rightPanelRef.current) {
      // When locking, sync left to right
      leftPanelRef.current.scrollLeft = rightPanelRef.current.scrollLeft;
      leftPanelRef.current.scrollTop = rightPanelRef.current.scrollTop;
    }
    setIsLocked(!isLocked);
  };

  // Resizer drag handlers
  const handleResizerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStartX(e.clientX);
    setResizeStartWidth(leftPanelWidth);
  };

  const handleResizerMouseMove = (e: React.MouseEvent) => {
    if (!isResizing || !containerRef.current) return;

    e.preventDefault();
    const containerRect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - resizeStartX;
    const containerWidth = containerRect.width;
    const deltaPercent = (deltaX / containerWidth) * 100;

    const newWidth = Math.max(20, Math.min(80, resizeStartWidth + deltaPercent));
    setLeftPanelWidth(newWidth);
  };

  const handleResizerMouseUp = () => {
    setIsResizing(false);
  };

  // Enhanced drag handlers for synchronized panning
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>, panel: 'left' | 'right') => {
    if (imageType !== 'original') return;

    const container = e.currentTarget;
    setActivePanel(panel);
    setDragState({
      isDragging: true,
      startX: e.pageX - container.offsetLeft,
      startY: e.pageY - container.offsetTop,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, panel: 'left' | 'right') => {
    if (!dragState.isDragging || imageType !== 'original' || activePanel !== panel) return;

    e.preventDefault();
    const container = e.currentTarget;
    const x = e.pageX - container.offsetLeft;
    const y = e.pageY - container.offsetTop;
    const walkX = (x - dragState.startX) * 2; // Multiply for faster scroll
    const walkY = (y - dragState.startY) * 2;

    const newScrollLeft = dragState.scrollLeft - walkX;
    const newScrollTop = dragState.scrollTop - walkY;

    container.scrollLeft = newScrollLeft;
    container.scrollTop = newScrollTop;

    // Sync with other panel if locked
    if (isLocked) {
      syncScrollPosition(panel, newScrollLeft, newScrollTop);
    }
  };

  const handleMouseUp = () => {
    setDragState(prev => ({ ...prev, isDragging: false }));
    setActivePanel(null);
  };

  const handleMouseLeave = () => {
    setDragState(prev => ({ ...prev, isDragging: false }));
    setActivePanel(null);
  };

  // Editor modal handlers
  const handleEditImage = (imageType: 'primary' | 'secondary') => {
    if (!asset) return;

    // Get the original URL for the image being edited
    const imageUrl = imageType === 'primary'
      ? (asset.primary_original_url || asset.primary_image)
      : (asset.secondary_original_url || asset.secondary_image);

    if (!imageUrl) {
      toast.error('No original image available to edit');
      return;
    }

    setEditorModal({
      isOpen: true,
      imageUrl,
      imageType
    });

    // Notify parent that modal is open
    onEditorModalStateChange?.(true);
  };

  const handleSaveCroppedImage = async (croppedUrl: string) => {
    if (!asset) return;

    try {
      const updateInput = editorModal.imageType === 'primary'
        ? { primary_cropped_url: croppedUrl }
        : { secondary_cropped_url: croppedUrl };

      await updateAsset(asset.id, updateInput);

      toast.success('Image cropped successfully');

      // Refetch the asset to show the updated image
      window.location.reload();
    } catch (error) {
      console.error('Error saving cropped image:', error);
      toast.error('Failed to save cropped image');
    }
  };

  const handleCloseEditor = () => {
    setEditorModal({
      isOpen: false,
      imageUrl: '',
      imageType: 'primary'
    });

    // Notify parent that modal is closed
    onEditorModalStateChange?.(false);
  };

  // Global mouse event listeners for resizing
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      e.preventDefault();
      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaX = e.clientX - resizeStartX;
      const containerWidth = containerRect.width;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const newWidth = Math.max(20, Math.min(80, resizeStartWidth + deltaPercent));
      setLeftPanelWidth(newWidth);
    };

    const handleGlobalMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isResizing, resizeStartX, resizeStartWidth]);

  const primaryImageUrl = getPrimaryImageUrl();
  const secondaryImageUrl = getSecondaryImageUrl();
  const { beforeVersion, afterVersion } = getChronologicalUrls();
  const { beforeImageType, afterImageType } = getActualImageTypes();

  // Determine if this is a diff (side-by-side) or single image
  const isDiff = asset?.comparison_mode === 'side_by_side' && secondaryImageUrl;

  // Handle loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading asset...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !asset) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-500">
          <p>{error || 'Asset not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Image Type Toggle and Sync Controls */}
      {!hideTypeToggle && (
        <div className="absolute top-2 left-2 z-20 bg-white/90 backdrop-blur-sm rounded-lg border border-gray-200 p-2 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Type:</span>
              <ToggleGroup
                type="single"
                value={imageType}
                onValueChange={(value) => value && setImageType(value as 'markup' | 'cropped' | 'original')}
                className="justify-start"
              >
                {availableTypes.markup && (
                  <ToggleGroupItem value="markup" className="text-xs px-2 py-1 h-6">
                    Markup
                  </ToggleGroupItem>
                )}
                {availableTypes.cropped && (
                  <ToggleGroupItem value="cropped" className="text-xs px-2 py-1 h-6">
                    Cropped
                  </ToggleGroupItem>
                )}
                {availableTypes.original && (
                  <ToggleGroupItem value="original" className="text-xs px-2 py-1 h-6">
                    Original
                  </ToggleGroupItem>
                )}
              </ToggleGroup>
            </div>

            {/* Lock/Sync Control - Only show in original mode for diffs */}
            {imageType === 'original' && isDiff && (
              <>
                <div className="border-l border-gray-300 h-4"></div>
                <div className="flex items-center gap-1">
                  <span className="text-xs font-medium text-gray-700">Sync:</span>
                  <Button
                    size="sm"
                    variant={isLocked ? "default" : "outline"}
                    onClick={handleLockToggle}
                    className="h-6 w-6 p-0"
                    title={isLocked ? "Synchronized panning (click to unlock)" : "Individual panning (click to sync)"}
                  >
                    {isLocked ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <Unlock className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Images - Flexible space */}
      <div className="flex-1 flex flex-col min-h-0">
        {imageType === 'original' ? (
          // Original mode - Side by side with drag and resizable divider
          isDiff ? (
            <div
              ref={containerRef}
              className="flex h-full gap-0"
              onMouseMove={handleResizerMouseMove}
              onMouseUp={handleResizerMouseUp}
            >
              {/* Before Image Panel */}
              <div
                className="flex flex-col min-h-0"
                style={{ width: `${leftPanelWidth}%` }}
              >
                {!hideHeader && (
                  <h4 className="flex-shrink-0 text-sm text-gray-500 p-2 flex items-center justify-center gap-2">
                    Before ({formatVersionDate(beforeVersion)})
                    {!hideEditButtons && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditImage(beforeImageType)}
                        className="ml-auto flex items-center gap-1 h-7 text-xs"
                      >
                        <Edit3 className="h-3 w-3" />
                        Edit
                      </Button>
                    )}
                  </h4>
                )}
                <div
                  ref={leftPanelRef}
                  className="flex-1 bg-gray-100 rounded-sm overflow-auto border border-gray-200 min-h-0"
                  style={{
                    cursor: dragState.isDragging && activePanel === 'left' ? 'grabbing' : (onImageClick ? 'pointer' : 'grab')
                  }}
                  onMouseDown={(e) => !onImageClick && handleMouseDown(e, 'left')}
                  onMouseMove={(e) => !onImageClick && handleMouseMove(e, 'left')}
                  onMouseUp={!onImageClick ? handleMouseUp : undefined}
                  onMouseLeave={!onImageClick ? handleMouseLeave : undefined}
                  onClick={() => onImageClick?.('before')}>
                  {secondaryImageUrl ? (
                    <img
                      src={secondaryImageUrl}
                      alt={`${asset.slug} - ${beforeVersion || 'Before'} - ${imageType}`}
                      className="w-full h-auto min-h-full object-top"
                      style={{ userSelect: 'none', pointerEvents: 'none' }}
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No before image
                    </div>
                  )}
                </div>
              </div>

              {/* Resizable Divider */}
              <div
                className="w-1 bg-gray-300 hover:bg-gray-400 cursor-col-resize flex items-center justify-center transition-colors"
                onMouseDown={handleResizerMouseDown}
                style={{ cursor: isResizing ? 'col-resize' : 'col-resize' }}
              >
                <GripVertical className="h-4 w-4 text-gray-500" />
              </div>

              {/* After Image Panel */}
              <div
                className="flex flex-col min-h-0"
                style={{ width: `${100 - leftPanelWidth}%` }}
              >
                {!hideHeader && (
                  <h4 className="flex-shrink-0 text-sm text-gray-500 p-2 flex items-center justify-center gap-2">
                    After ({formatVersionDate(afterVersion) || 'Latest'})
                    {!hideEditButtons && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditImage(afterImageType)}
                        className="ml-auto flex items-center gap-1 h-7 text-xs"
                      >
                        <Edit3 className="h-3 w-3" />
                        Edit
                      </Button>
                    )}
                  </h4>
                )}
                <div
                  ref={rightPanelRef}
                  className="flex-1 bg-gray-100 rounded-sm overflow-auto border border-gray-200 min-h-0"
                  style={{
                    cursor: dragState.isDragging && activePanel === 'right' ? 'grabbing' : (onImageClick ? 'pointer' : 'grab')
                  }}
                  onMouseDown={(e) => !onImageClick && handleMouseDown(e, 'right')}
                  onMouseMove={(e) => !onImageClick && handleMouseMove(e, 'right')}
                  onMouseUp={!onImageClick ? handleMouseUp : undefined}
                  onMouseLeave={!onImageClick ? handleMouseLeave : undefined}
                  onClick={() => onImageClick?.('after')}>
                  {primaryImageUrl ? (
                    <img
                      src={primaryImageUrl}
                      alt={`${asset.slug} - ${afterVersion || 'After'} - ${imageType}`}
                      className="w-full h-auto min-h-full object-top"
                      style={{ userSelect: 'none', pointerEvents: 'none' }}
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No after image
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Single image for non-diff assets
            <div
              className="flex-1 bg-gray-100 rounded-sm overflow-hidden border border-gray-200 min-h-0 flex items-center justify-center p-4"
              style={{ cursor: onImageClick ? 'pointer' : 'default' }}
              onClick={() => onImageClick?.('after')}>
              {primaryImageUrl ? (
                <img
                  src={primaryImageUrl}
                  alt={`${asset.slug} - ${afterVersion || asset.version} - ${imageType}`}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No {imageType} image available
                </div>
              )}
            </div>
          )
        ) : isDiff ? (
          // Side-by-side comparison - Full width
          <div className="grid grid-cols-2 gap-1 h-full">
            <div className="flex flex-col min-h-0 h-full">
              {!hideHeader && (
                <h4 className="flex-shrink-0 text-sm text-gray-500 p-2 flex items-center justify-center gap-2">
                  Before ({formatVersionDate(beforeVersion)})
                  {!hideEditButtons && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditImage(beforeImageType)}
                      className="ml-auto flex items-center gap-1 h-7 text-xs"
                    >
                      <Edit3 className="h-3 w-3" />
                      Edit
                    </Button>
                  )}
                </h4>
              )}
              <div
                className="flex-1 bg-gray-100 rounded-sm overflow-hidden border border-gray-200 min-h-0 flex items-center justify-center p-4"
                style={{ cursor: onImageClick ? 'pointer' : 'default' }}
                onClick={() => onImageClick?.('before')}>
                {secondaryImageUrl ? (
                  <img
                    src={secondaryImageUrl}
                    alt={`${asset.slug} - ${beforeVersion} - ${imageType}`}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No {imageType} image available
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col min-h-0 h-full">
              {!hideHeader && (
                <h4 className="flex-shrink-0 text-sm text-gray-500 p-2 flex items-center justify-center gap-2">
                  After ({formatVersionDate(afterVersion) || 'Latest'})
                  {!hideEditButtons && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditImage(afterImageType)}
                      className="ml-auto flex items-center gap-1 h-7 text-xs"
                    >
                      <Edit3 className="h-3 w-3" />
                      Edit
                    </Button>
                  )}
                </h4>
              )}
              <div
                className="flex-1 bg-gray-100 rounded-sm overflow-hidden border border-gray-200 min-h-0 flex items-center justify-center p-4"
                style={{ cursor: onImageClick ? 'pointer' : 'default' }}
                onClick={() => onImageClick?.('after')}>
                {primaryImageUrl ? (
                  <img
                    src={primaryImageUrl}
                    alt={`${asset.slug} - ${afterVersion || 'Latest'} - ${imageType}`}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No {imageType} image available
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Single image - Full width (non-original modes)
          <div className="flex flex-col h-full min-h-0">
            {!hideHeader && (
              <h4 className="flex-shrink-0 text-sm text-gray-500 mb-2 flex items-center justify-center gap-2 px-2">
                {formatVersionDate(afterVersion || asset.version)}
                {!hideEditButtons && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditImage(afterImageType)}
                    className="ml-auto flex items-center gap-1 h-7 text-xs"
                  >
                    <Edit3 className="h-3 w-3" />
                    Edit
                  </Button>
                )}
              </h4>
            )}
            <div
              className="flex-1 bg-gray-100 rounded-sm overflow-hidden border border-gray-200 min-h-0 flex items-center justify-center p-4"
              style={{ cursor: onImageClick ? 'pointer' : 'default' }}
              onClick={() => onImageClick?.('after')}>
              {primaryImageUrl ? (
                <img
                  src={primaryImageUrl}
                  alt={`${asset.slug} - ${afterVersion || asset.version} - ${imageType}`}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No {imageType} image available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Cloudinary Editor Modal */}
      <CloudinaryEditorModal
        isOpen={editorModal.isOpen}
        imageUrl={editorModal.imageUrl}
        onSave={handleSaveCroppedImage}
        onClose={handleCloseEditor}
        title={`Edit ${editorModal.imageType === 'primary' ? 'After' : 'Before'} Image`}
      />
    </div>
  );
};